import React from 'react';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { Status } from '../../../../types/redux/data/dataTypes';
import { formatESGWithUnit } from '../../../../utilities/numberFormatters';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import { IndicatorComplianceTableData } from './IndicatorCompliance.component';
import SecondLevelTable from './SecondLevelTable.component';
import makeStyles from '@mui/styles/makeStyles';
import { mainColors } from '../../../../styling/theme';
import type { IndicatorCompliancePayload } from '../../../../api/rs-hg/esg';

interface IndicatorComplianceTableProps {
  positionDate: string | undefined;
  fundId: string;
  data: IndicatorCompliancePayload;
}

const useStyles = makeStyles(() => ({
  tabsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '1.25rem',
    width: '100%',
  },
  tab_active: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '1.12rem',
    backgroundColor: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
  },
  tab_inactive: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '1.12rem',
    backgroundColor: 'white',
    color: mainColors.mainBlue,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: mainColors.hoverOverWhite,
    },
  },
}));

const standardColumns: CustomColumn<IndicatorComplianceTableData>[] = [
  {
    title: 'Indicator',
    field: 'indicator',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '10%',
  },
  {
    title: 'Name',
    field: 'indicator_name',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '15%',
  },
  {
    title: 'Description',
    field: 'description',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '45%',
  },
  {
    title: 'Limit Type',
    field: 'limit_type',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '10%',
  },
  {
    title: 'Limit',
    field: 'limit',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => formatESGWithUnit(rowData.limit, rowData.unit),
    width: '10%',
  },
  {
    title: 'Value',
    field: 'value',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => formatESGWithUnit(rowData.value, rowData.unit),
    width: '10%',
  },
  {
    title: 'Pass / Fail',
    field: 'pass_fail',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => (
      <RaptorStatusBox status={rowData.pass_fail as Status} />
    ),
    width: '10%',
  },
];

const buildColumns = (
  data: IndicatorCompliancePayload,
  columns: CustomColumn<IndicatorComplianceTableData>[],
): CustomColumn<IndicatorComplianceTableData>[] => {
  if (!data.configs?.indicator_columns) {
    return columns;
  }

  const returnColumns: CustomColumn<IndicatorComplianceTableData>[] = [];
  data.configs.indicator_columns.forEach((column: string) => {
    columns.forEach((col: CustomColumn<IndicatorComplianceTableData>) => {
      if (col.field === column) {
        returnColumns.push(col);
      }
    });
  });
  return returnColumns;
};

const buildTableData = (
  data: IndicatorCompliancePayload,
  tab: string,
): IndicatorComplianceTableData[] => {
  if (
    !data.data?.length ||
    !data.configs.indicators ||
    !data.configs.indicators[tab]
  ) {
    return [];
  }

  const indicators = data.configs.indicators[tab];
  const returnData: IndicatorComplianceTableData[] = [];
  data.data.forEach((row) => {
    if (
      Object.keys(row).includes('indicator') &&
      indicators.includes(row.indicator)
    ) {
      returnData.push(row as IndicatorComplianceTableData);
    }
  });
  return returnData;
};

const IndicatorComplianceTable: React.FC<IndicatorComplianceTableProps> = ({
  positionDate,
  fundId,
  data,
}) => {
  const classes = useStyles();

  const [tabs] = React.useState<string[]>(
    Object.keys(data.configs.indicators),
  );
  const [activeTab, setActiveTab] = React.useState<string>(
    Object.keys(data.configs.indicators)[0],
  );

  return (
    <GridItem xs={12} card>
      <CustomTable<IndicatorComplianceTableData>
        columns={buildColumns(data, standardColumns)}
        showToolbar
        data={buildTableData(data, activeTab)}
        title={
          tabs.length > 1 ? (
            <div className={classes.tabsContainer}>
              {Object.keys(data.configs.indicators).map((tab: string) => {
                return (
                  <div
                    key={tab}
                    className={
                      activeTab === tab
                        ? classes.tab_active
                        : classes.tab_inactive
                    }
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </div>
                );
              })}
            </div>
          ) : (
            activeTab
          )
        }
        pdfTitle={`${activeTab}`}
        options={{
          draggable: false,
          sorting: false,
          paging: false,
          search: true,
          exportButton: true,
        }}
        detailPanel={[
          {
            disabled: false,
            render: (rowData: IndicatorComplianceTableData) => {
              return (
                <SecondLevelTable
                  data={data}
                  rowData={rowData}
                  positionDate={positionDate}
                  fundId={fundId}
                />
              );
            },
          },
        ]}
      />
    </GridItem>
  );
};

export default IndicatorComplianceTable;
